import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  CalendarClock,
  CheckCircle2,
  ClipboardCheck,
  Play,
  RotateCcw,
  ShieldCheck,
  UserCheck,
  UsersRound,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { httpServices as services } from "@/services";
import type { VisitPlan, VisitPlanTarget } from "@/types/domain";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { ErrorState, LoadingState } from "@/components/shared/StatePanels";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

type TargetAction = { target: VisitPlanTarget; mode: "MISSED" | "RESCHEDULED" } | undefined;

export default function VisitPlanDetailPage() {
  const { planId = "" } = useParams();
  const [plan, setPlan] = useState<VisitPlan>();
  const [error, setError] = useState("");
  const [working, setWorking] = useState(false);
  const [targetAction, setTargetAction] = useState<TargetAction>();
  const [missedReason, setMissedReason] = useState("");
  const [rescheduledDate, setRescheduledDate] = useState("");
  const [targetNote, setTargetNote] = useState("");

  const load = useCallback(async () => {
    try {
      const record = await services.visits.getPlan(planId);
      if (!record) setError("Visit Plan was not found.");
      else {
        setPlan(record);
        setError("");
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Visit Plan could not be loaded.");
    }
  }, [planId]);

  useEffect(() => { void load(); }, [load]);

  if (error) return <ErrorState message={error} />;
  if (!plan) return <LoadingState label="Loading Visit Plan" />;

  const sanction = async () => {
    setWorking(true);
    try {
      const updated = await services.visits.sanctionPlan(plan.id, "Approved field agenda");
      setPlan(updated);
      toast.success("Visit Plan sanctioned", { description: "It is now ready for the assigned field staff to acknowledge." });
    } catch (reason) {
      toast.error(reason instanceof Error ? reason.message : "Visit Plan could not be sanctioned.");
    } finally {
      setWorking(false);
    }
  };

  const acknowledge = async () => {
    setWorking(true);
    try {
      const updated = await services.visits.acknowledgePlan(plan.id, "Field agenda acknowledged");
      setPlan(updated);
      toast.success("Visit Plan acknowledged", { description: "The assigned field staff can now start individual farmer visits." });
    } catch (reason) {
      toast.error(reason instanceof Error ? reason.message : "Visit Plan could not be acknowledged.");
    } finally {
      setWorking(false);
    }
  };

  const applyTargetAction = async () => {
    if (!targetAction) return;
    if (targetAction.mode === "MISSED" && !missedReason.trim()) {
      toast.error("Give a reason for the missed visit.");
      return;
    }
    if (targetAction.mode === "RESCHEDULED" && !rescheduledDate) {
      toast.error("Choose the new visit date.");
      return;
    }
    setWorking(true);
    try {
      await services.visits.updatePlanTarget(plan.id, targetAction.target.id, {
        status: targetAction.mode,
        missedReason: targetAction.mode === "MISSED" ? missedReason.trim() : undefined,
        rescheduledToDate: targetAction.mode === "RESCHEDULED" ? rescheduledDate : undefined,
        notes: targetNote.trim() || undefined,
      });
      setTargetAction(undefined);
      setMissedReason("");
      setRescheduledDate("");
      setTargetNote("");
      await load();
      toast.success(targetAction.mode === "MISSED" ? "Target marked missed" : "Target rescheduled");
    } catch (reason) {
      toast.error(reason instanceof Error ? reason.message : "Target could not be updated.");
    } finally {
      setWorking(false);
    }
  };

  const canStartPlanTargets = ["ACKNOWLEDGED", "IN_PROGRESS"].includes(plan.status);

  return (
    <div className="space-y-6" data-testid="visit-plan-detail-page">
      <PageHeader
        eyebrow="Field Visits · Plan"
        title={plan.title}
        description={`${plan.planCode} · ${plan.plannedStartDate}${plan.plannedEndDate !== plan.plannedStartDate ? ` → ${plan.plannedEndDate}` : ""}`}
        backTo="/visits"
        action={
          <div className="flex flex-wrap gap-2">
            {plan.status === "DRAFT" && (
              <Button onClick={sanction} disabled={working} data-testid="visit-plan-sanction">
                <ShieldCheck className="mr-2 size-4" />Sanction Plan
              </Button>
            )}
            {plan.status === "SANCTIONED" && (
              <Button onClick={acknowledge} disabled={working} data-testid="visit-plan-acknowledge">
                <CheckCircle2 className="mr-2 size-4" />Acknowledge Plan
              </Button>
            )}
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <StatusBadge status={plan.status} testId="visit-plan-detail-status" />
        <span className="text-sm text-muted-foreground">
          Assigned to <span className="font-semibold text-foreground">{plan.assignedStaffName || "staff record unavailable"}</span>
        </span>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card><CardContent className="p-5"><UsersRound className="size-5 text-primary" /><p className="mt-3 text-3xl font-semibold">{plan.targetCount}</p><p className="text-sm text-muted-foreground">Farmers scheduled</p></CardContent></Card>
        <Card><CardContent className="p-5"><UserCheck className="size-5 text-primary" /><p className="mt-3 text-3xl font-semibold">{plan.visitedCount}</p><p className="text-sm text-muted-foreground">Visited</p></CardContent></Card>
        <Card><CardContent className="p-5"><CalendarClock className="size-5 text-warning" /><p className="mt-3 text-3xl font-semibold">{plan.remainingCount}</p><p className="text-sm text-muted-foreground">Remaining / rescheduled</p></CardContent></Card>
        <Card><CardContent className="p-5"><XCircle className="size-5 text-destructive" /><p className="mt-3 text-3xl font-semibold">{plan.missedCount}</p><p className="text-sm text-muted-foreground">Missed</p></CardContent></Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Plan accountability</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 text-sm md:grid-cols-3">
          <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Sanctioned</p><p className="mt-1 font-medium">{plan.sanctionedAt ? new Date(plan.sanctionedAt).toLocaleString() : "Not yet sanctioned"}</p></div>
          <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Acknowledged</p><p className="mt-1 font-medium">{plan.acknowledgedAt ? new Date(plan.acknowledgedAt).toLocaleString() : "Not yet acknowledged"}</p></div>
          <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Coordinator note</p><p className="mt-1 font-medium">{plan.notes || "No additional instruction"}</p></div>
        </CardContent>
      </Card>

      {targetAction && (
        <Card className="border-primary/25" data-testid="visit-target-action-panel">
          <CardHeader>
            <CardTitle className="text-base">
              {targetAction.mode === "MISSED" ? "Mark visit target as missed" : "Reschedule visit target"} · {targetAction.target.farmerName}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {targetAction.mode === "MISSED" ? (
              <div className="space-y-2">
                <Label htmlFor="missed-reason">Reason</Label>
                <Textarea id="missed-reason" value={missedReason} onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setMissedReason(event.target.value)} placeholder="Why was this farmer not visited?" />
              </div>
            ) : (
              <div className="space-y-2 max-w-sm">
                <Label htmlFor="rescheduled-date">New visit date</Label>
                <Input id="rescheduled-date" type="date" value={rescheduledDate} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setRescheduledDate(event.target.value)} />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="target-note">Note</Label>
              <Textarea id="target-note" value={targetNote} onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setTargetNote(event.target.value)} placeholder="Optional field/programme note" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setTargetAction(undefined)}>Cancel</Button>
              <Button type="button" onClick={applyTargetAction} disabled={working}>Save target status</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><ClipboardCheck className="size-4 text-primary" />Farmer agenda</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Farmer / Farm</TableHead>
                  <TableHead>Planned date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actual visit</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plan.targets.map((target, index) => {
                  const canStart = canStartPlanTargets && ["ACKNOWLEDGED", "RESCHEDULED"].includes(target.status);
                  const canAdjust = ["PLANNED", "ACKNOWLEDGED", "RESCHEDULED"].includes(target.status);
                  return (
                    <TableRow key={target.id}>
                      <TableCell className="text-muted-foreground">{target.sequenceNo || index + 1}</TableCell>
                      <TableCell>
                        <p className="font-semibold">{target.farmerName || "Farmer"}</p>
                        <p className="text-xs text-muted-foreground">{target.farmName || "Farm"}</p>
                      </TableCell>
                      <TableCell>{target.rescheduledToDate || target.plannedDate || "Within Plan range"}</TableCell>
                      <TableCell><StatusBadge status={target.status} testId={`visit-target-status-${target.id}`} /></TableCell>
                      <TableCell>
                        {target.actualVisitId ? (
                          <Link to={`/visits/${target.actualVisitId}`} className="font-medium text-primary hover:underline">Open visit</Link>
                        ) : (
                          <span className="text-xs text-muted-foreground">Not started</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          {canStart && (
                            <Button size="sm" asChild>
                              <Link to={`/visits/plans/${plan.id}/targets/${target.id}/start`}>
                                <Play className="mr-1 size-3.5" />Start
                              </Link>
                            </Button>
                          )}
                          {canAdjust && (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => setTargetAction({ target, mode: "RESCHEDULED" })}>
                                <RotateCcw className="mr-1 size-3.5" />Reschedule
                              </Button>
                              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setTargetAction({ target, mode: "MISSED" })}>
                                Missed
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {plan.targets.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">No farmers are attached to this Visit Plan.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
